import { useCallback, useEffect, useMemo, useState } from 'react'
import ReactPaginate from 'react-paginate'
import { PageTitle } from '../../../../../_metronic/layout/core'
import {
  declineMatchingPaymentTicket,
  finishMatchingPaymentTicket,
  getMatchingPaymentTickets,
} from '../../core/_requests'
import {
  MatchingPaymentTicket,
  MatchingPayoutFinishedTicketsRequestParams,
  MatchingPaymentFinishedTicketsFilters,
  MatchingPaymentFinishedTicketsRequestParams,
  PaginationState,
  MatchingPaymentStatusOption,
  getMatchingPaymentStatusOption,
  isMatchingPaymentTicketStatus,
  canUser,
  matchingCapabilities,
  getVisibleFinishedPaymentColumns,
  MatchingFile,
} from '../../core/_models'
import FinishedPaymentTicketItem from './FinishedPaymentTicketItem'
import FinishedPaymentTicketsFilter from './FinishedPaymentTicketsFilter'
import { Table } from '../../../../../components/Table'
import { TableHead } from '../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../components/Table/TableBody'
import { IsGranted, ROLE, useAuth } from '../../../auth'
import { EMatchingPaymentTicketStatus } from '../../../../../types/enums'
import { convertToUTCByMSK } from '../../../../../utils'

import '../../../../assets/css/pagination.css'
import StatusModal from '../common/StatusModal'
import Swal from 'sweetalert2'
import { getTicketActionErrorMessage } from '../../core/_mocks'
import { matchingFinishedPaymentsConfig } from './config'
import { Header } from '../common/Header'
import { Forbidden } from '../common/Forbidden'

const { defaultFilters, breadcrumbs, perPage } = matchingFinishedPaymentsConfig

const FinishedPaymentTickets = () => {
  const { currentUser } = useAuth()

  // Права и видимость
  const canAccess = useMemo(
    () => canUser(currentUser, matchingCapabilities.canViewFinishedPayments),
    [currentUser]
  )
  const canManageTickets = useMemo(
    () => canAccess && canUser(currentUser, matchingCapabilities.canEditTickets),
    [currentUser, canAccess]
  )
  // Право на операции с чеками — то же, что в активной очереди (canEditTickets):
  // бэкенд гейтит verify/reject одним и тем же списком ролей на всех поверхностях.
  const canManageBills = useMemo(
    () => canAccess && canUser(currentUser, matchingCapabilities.canEditTickets),
    [currentUser, canAccess]
  )
  // CPAYMENT-1750: откат COMPLETED → EXPIRED закрыт оператору мэтчинга на уровне воркфлоу —
  // без этого гейта он видел бы опцию и получал 422.
  const canRollbackCompleted = useMemo(
    () => canManageTickets && canUser(currentUser, matchingCapabilities.canRollbackCompleted),
    [currentUser, canManageTickets]
  )
  const visibility = useMemo(() => getVisibleFinishedPaymentColumns(currentUser), [currentUser])
  const visibleColumns = useMemo(() => Object.keys(visibility).filter(Boolean), [visibility])
  // Переотправка КБ — отдельное право от matching-прав; список ролей в matchingCapabilities.
  const canResend = useMemo(
    () => IsGranted(matchingCapabilities.canResendCallback, currentUser),
    [currentUser]
  )
  const canChangeAmount = useMemo(
    () => IsGranted(matchingCapabilities.canChangeCreditedAmount, currentUser),
    [currentUser]
  )
  const hasActions = canResend || canChangeAmount
  // CPAYMENT-1690: ссылка бейджа ведёт в журнал диспутов, а он открыт шире, чем ресенд КБ.
  // Раньше оба признака брались из canResend — теперь это разные права, не сливать обратно.
  const canOpenDisputeJournal = useMemo(
    () => IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR], currentUser),
    [currentUser]
  )

  // Состояния
  const [tickets, setTickets] = useState<MatchingPaymentTicket[]>([])
  const [filters, setFilters] = useState<MatchingPaymentFinishedTicketsFilters>(defaultFilters)
  const [countPages, setCountPages] = useState(0)
  const [fullCount, setFullCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoading, setLoading] = useState(false)
  const [statusModalTicket, setStatusModalTicket] = useState<MatchingPaymentTicket | null>(null)

  /**
   * Загрузка списка тикетов
   */
  const loadTicketList = useCallback(
    async (query: MatchingPayoutFinishedTicketsRequestParams) => {
      if (!canAccess) return
      setLoading(true)
      try {
        const response = await getMatchingPaymentTickets(query)
        if (!response?.data) {
          return false
        }
        if (response.data.success && response.data.list) {
          setTickets(response.data.list)
          setCountPages(Math.ceil(response.data.totalRows / perPage))
          setFullCount(response.data.totalRows)
          setCurrentPage(query.page ? query.page : 1)
        }
      } catch (err) {
        console.error('error catched: ', err)
      } finally {
        setLoading(false)
      }
    },
    [canAccess]
  )

  /**
   * Применение фильтров (подготовка запроса и отправка запроса на сервер)
   */
  const applyFilters = useCallback(
    (newFilters: MatchingPaymentFinishedTicketsFilters, pagination?: PaginationState) => {
      if (!canAccess) return

      setFilters(newFilters)
      const { byCompletionDate, dateStart, dateEnd, merchantTokenList, statusList, search } =
        newFilters

      // Формируем query только из поддерживаемых swagger-полей
      const query: MatchingPaymentFinishedTicketsRequestParams = {
        search,
        merchantTokenList,
        statusList,
        page: pagination?.page || 1,
        perPage: pagination?.perPage || perPage,
      }

      const safeDateStart = dateStart instanceof Date ? dateStart : new Date(dateStart)
      const safeDateEnd = dateEnd instanceof Date ? dateEnd : new Date(dateEnd)

      if (byCompletionDate) {
        query.completionDateStart = convertToUTCByMSK(safeDateStart)
        query.completionDateEnd = convertToUTCByMSK(safeDateEnd)
      } else {
        query.createdAtStart = convertToUTCByMSK(safeDateStart)
        query.createdAtEnd = convertToUTCByMSK(safeDateEnd)
      }

      loadTicketList(query)
    },
    [loadTicketList, canAccess]
  )

  const setTicketStatus = useCallback(
    async (
      ticket: MatchingPaymentTicket | null,
      status?: MatchingPaymentStatusOption | EMatchingPaymentTicketStatus | undefined
    ) => {
      if (!canManageTickets || !ticket) return

      const statusValue = typeof status === 'string' ? status : status?.value
      const safeStatus =
        isMatchingPaymentTicketStatus(statusValue || ticket?.status) || ticket?.status
      setTickets((prev) =>
        prev.map((t) =>
          t?.ticketToken === ticket?.ticketToken ? { ...t, ...ticket, status: safeStatus } : t
        )
      )
    },
    [canManageTickets]
  )

  // Чек прикреплён — обновляем строку локально, без полного рефетча списка
  // (то же поведение, что в активной очереди).
  const updateBillForTicket = useCallback(
    (token: string, file: MatchingFile) => {
      setTickets((prev) =>
        prev.map((t) => (t?.ticketToken === token ? { ...t, files: [file] } : t))
      )
    },
    []
  )

  // Решение по чеку на завершённом тикете меняет только сам файл: статус тикета и
  // сумма зачисления остаются прежними, поэтому строка из списка не убирается.
  const updateBillStatus = useCallback(
    (token: string, billId: MatchingFile['id'], status: MatchingFile['status']) => {
      setTickets((prev) =>
        prev.map((t) =>
          t?.ticketToken === token
            ? { ...t, files: (t.files || []).map((f) => (f.id === billId ? { ...f, status } : f)) }
            : t
        )
      )
    },
    []
  )

  // The new credited amount is the one the server applied; the row stays in the list.
  const updateTicketAmount = useCallback((token: string, amount: number) => {
    setTickets((prev) => prev.map((t) => (t?.ticketToken === token ? { ...t, amount } : t)))
  }, [])

  const handleExpireTicket = useCallback(
    async (ticket: MatchingPaymentTicket | null, amount?: number) => {
      if (!canManageTickets || !ticket) return

      try {
        const response = await declineMatchingPaymentTicket(ticket?.ticketToken, currentUser)

        if (response?.status !== 200) {
          throw getTicketActionErrorMessage(response)
        }

        const hasUpdate = response?.data?.ticket && typeof response?.data?.ticket === 'object'
        const update = { ...ticket }

        if (hasUpdate) {
          update.amount = response.data.ticket?.amount
          update.status = response.data.ticket?.status
        }

        if (update.amount !== null && typeof update.amount !== 'number') {
          update.amount = amount ?? null
        }

        setTicketStatus(update, update?.status || EMatchingPaymentTicketStatus.EXPIRED)
      } catch (error) {
        Swal.fire('Ошибка', getTicketActionErrorMessage(error), 'error')
      }
    },
    [setTicketStatus, currentUser, canManageTickets]
  )

  const handleCompleteTicket = useCallback(
    async (ticket: MatchingPaymentTicket | null, amount: number) => {
      if (!canManageTickets || !ticket || !ticket?.externalUserId) return
      try {
        const response = await finishMatchingPaymentTicket(
          ticket?.ticketToken,
          currentUser,
          String(ticket?.externalUserId),
          amount
        )
        if (response?.status !== 200) {
          throw getTicketActionErrorMessage(response)
        }

        const hasUpdate = response?.data?.ticket && typeof response?.data?.ticket === 'object'
        const update = { ...ticket }

        if (hasUpdate) {
          update.amount = response.data.ticket?.amount
          update.status = response.data.ticket?.status
        }

        if (update.amount !== null && typeof update.amount !== 'number') {
          update.amount = amount ?? null
        }

        setTicketStatus(update, update?.status || EMatchingPaymentTicketStatus.COMPLETED)
      } catch (error) {
        Swal.fire('Ошибка', getTicketActionErrorMessage(error), 'error')
      }
    },
    [setTicketStatus, currentUser, canManageTickets]
  )

  const handleCloseStatusModel = useCallback(() => {
    setStatusModalTicket(null)
  }, [])

  const handleChangeTicketStatus = useCallback(
    async (ticket: MatchingPaymentTicket | null, status: MatchingPaymentStatusOption) => {
      if (!canManageTickets || !ticket) return
      switch (status.value) {
        case EMatchingPaymentTicketStatus.COMPLETED:
          setStatusModalTicket({ ...ticket, status: EMatchingPaymentTicketStatus.COMPLETED })
          break
        case EMatchingPaymentTicketStatus.EXPIRED:
          await handleExpireTicket(ticket)
          break
        default:
          const validStatus = getMatchingPaymentStatusOption(status?.value)
          if (validStatus) {
            setTicketStatus(ticket, validStatus)
          } else {
            console.warn(`Не реализовано изменение статуса на «${status.label}»`, ticket)
          }
          break
      }
    },
    [setTicketStatus, handleExpireTicket, canManageTickets]
  )

  useEffect(() => {
    applyFilters(defaultFilters, { page: 1, perPage })
  }, [applyFilters])

  return (
    <div style={{ position: 'relative' }}>
      <StatusModal
        ticket={statusModalTicket}
        onSubmit={handleCompleteTicket}
        onClose={handleCloseStatusModel}
      />

      <div className='card mb-7'>
        <PageTitle breadcrumbs={breadcrumbs}>Завершённые пополнения</PageTitle>
        <Header title='Завершённые пополнения' count={fullCount} />
      </div>

      {!canAccess && <Forbidden />}

      {!!canAccess && (
        <FinishedPaymentTicketsFilter
          defaultFilters={defaultFilters}
          applyFilters={applyFilters}
          visibility={visibility}
        />
      )}

      {!!canAccess && (
        <div className='card'>
          <div className='card-body py-3'>
            <div className='table-responsive'>
              <Table>
              <TableHead>
                <tr>
                  <th style={{ width: 32 }} className='align-top'></th>
                  {visibility.ticketToken ? (
                    <th className='min-w-100px align-top'>
                      ID тикета <br />
                      <small>ID транзакции</small>
                    </th>
                  ) : null}
                  {visibility.amount ? <th className='min-w-80px align-top'>Сумма</th> : null}
                  {visibility.paymentMethod ? (
                    <th className='min-w-70px align-top'>Платежный метод</th>
                  ) : null}
                  {visibility.createdDate ? (
                    <th className='min-w-100px align-top'>Дата создания</th>
                  ) : null}
                  {visibility.completionDate ? (
                    <th className='min-w-100px align-top'>Дата закрытия</th>
                  ) : null}
                  {visibility.client ? <th className='min-w-150px align-top'>Клиент</th> : null}
                  {visibility.merchant ? <th className='min-w-120px align-top'>Мерчант</th> : null}
                  {visibility.executor ? (
                    <th className='min-w-100px align-top'>Исполнитель</th>
                  ) : null}
                  {visibility.requisite ? (
                    <th className='min-w-150px align-top'>Реквизиты</th>
                  ) : null}
                  {visibility.file ? (
                    <th className='min-w-80px align-top text-center'>Чек</th>
                  ) : null}
                  {visibility.antifraud ? (
                    <th className='min-w-60px text-center align-top'>Антифрод</th>
                  ) : null}
                  {visibility.status ? (
                    <th className='min-w-200px text-center align-top'>Статус</th>
                  ) : null}
                  {hasActions ? (
                    <th className='min-w-80px text-end align-top'>Действия</th>
                  ) : null}
                </tr>
              </TableHead>
              <TableBody>
                {Array.isArray(tickets) && tickets?.length > 0 ? (
                  tickets?.map((ticket: MatchingPaymentTicket, index) => {
                    return (
                      <FinishedPaymentTicketItem
                        ticket={ticket}
                        key={ticket?.ticketToken + index}
                        onStatusChange={handleChangeTicketStatus}
                        visibility={visibility}
                        visibleColumns={visibleColumns}
                        visibleColumnsCount={visibleColumns?.length || 0}
                        canManageTickets={canManageTickets}
                        canManageBills={canManageBills}
                        updateBillForTicket={updateBillForTicket}
                        updateBillStatus={updateBillStatus}
                        canRollbackCompleted={canRollbackCompleted}
                        canResend={canResend}
                        canChangeAmount={canChangeAmount}
                        onAmountChanged={updateTicketAmount}
                        canOpenDisputeJournal={canOpenDisputeJournal}
                      />
                    )
                  })
                ) : (
                  <tr>
                    <td className='text-center' colSpan={(visibleColumns?.length || 0) + 1 + (hasActions ? 1 : 0)}>
                      {isLoading ? 'Загрузка...' : 'Тикеты не найдены'}
                    </td>
                  </tr>
                )}
              </TableBody>
            </Table>
            </div>
          </div>
        </div>
      )}

      {canAccess && countPages > 1 && (
        <ReactPaginate
          activeClassName={'active-pagination '}
          breakClassName={'item-pagination break-me-pagination '}
          containerClassName={'pagination-pagination'}
          disabledClassName={'disabled-page-pagination'}
          nextClassName={'item-pagination ki-duotone ki-right fs-2x'}
          pageClassName={'item-pagination pagination-page-pagination '}
          previousClassName={'item-pagination ki-duotone ki-left fs-2x'}
          breakLabel='...'
          nextLabel=''
          onPageChange={(pageNumber) => {
            applyFilters({ ...filters }, { page: pageNumber.selected + 1, perPage })
          }}
          pageRangeDisplayed={5}
          pageCount={countPages || 1}
          previousLabel=''
          renderOnZeroPageCount={null}
          forcePage={currentPage - 1}
        />
      )}
    </div>
  )
}

export default FinishedPaymentTickets
